/**
 * `<ElapsedTickSpan>` — a leaf component that renders an agent's
 * elapsed time and owns the 1Hz `useElapsedTick` heartbeat locally.
 *
 * Per `docs/c3-agents-panel-design.md` §9 Rule 3 (and C2's matching
 * `docs/c2-transparency-banner-design.md` §9 Rule 3), the 1Hz tick is
 * isolated to a leaf so the per-second re-render never bubbles to the
 * memoised row / banner view. If the parent hosted the tick, the
 * `React.memo` budget would be blown immediately — every running row
 * would re-render once a second regardless of comparator return value
 * (state changes inside a component always trigger that component's
 * re-render, memo only suppresses parent-driven re-renders).
 *
 * Originally inlined in `ActivityBanner.tsx` (#668); lifted here as
 * part of C3 Issue C (#689) so the panel rows (`AgentRowCard`,
 * `MainAgentRowCard`) and the banner share one implementation.
 *
 * Behaviour pinned by tests:
 *   - `stoppedAt === null` → tick is live; component re-renders once a
 *     second so the `Xm Ys` text advances.
 *   - `stoppedAt !== null` → tick is a no-op (the hook clears its
 *     interval) and the elapsed value freezes at `stoppedAt -
 *     startedAt`.
 *
 * Styling uses `tabular-nums` so the per-second tick doesn't reflow the
 * surrounding row layout — a fixed-width digit glyph keeps the column
 * aligned even when the seconds digits change width visually.
 */

import React from 'react';
import { useElapsedTick } from '../hooks/useElapsedTick';

export interface ElapsedTickSpanProps {
    /** Epoch ms when the agent's `(subsystem, action)` last changed —
     *  i.e. the anchor for the elapsed counter. */
    startedAt: number;
    /** Epoch ms when the agent went terminal, or `null` while
     *  running. When null, the tick is live; otherwise the elapsed
     *  value freezes. */
    stoppedAt: number | null;
    /**
     * Optional `data-testid` override. Defaults to
     * `elapsed-tick-span`. The two known call-sites — banner +
     * panel rows — set this so their respective tests can target the
     * span without colliding (the banner's existing testid is
     * `activity-banner-elapsed`).
     */
    testId?: string;
    /**
     * Optional className override. Defaults to the design §4 row
     * spec: `text-[11px] tabular-nums text-white/40`. Banner callers
     * pass their own value (`text-xs tabular-nums text-white/50`) to
     * preserve C2's visual contract.
     */
    className?: string;
}

const DEFAULT_CLASS_NAME = 'text-[11px] tabular-nums text-white/40';

/**
 * Format an elapsed duration as `12s` / `4m 12s`. Pure helper exported
 * for tests that want to verify the displayed text without parsing the
 * DOM. Mirrors `ActivityBanner.tsx:formatElapsed` so the two surfaces
 * agree on the wire-to-string conversion.
 */
export function formatElapsedMs(
    startedAt: number,
    stoppedAt: number | null,
    now: number = Date.now(),
): string {
    const end = stoppedAt ?? now;
    const ms = Math.max(0, end - startedAt);
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remSec = seconds % 60;
    return remSec === 0 ? `${minutes}m` : `${minutes}m ${remSec}s`;
}

export function ElapsedTickSpan({
    startedAt,
    stoppedAt,
    testId = 'elapsed-tick-span',
    className = DEFAULT_CLASS_NAME,
}: ElapsedTickSpanProps): React.JSX.Element {
    const isRunning = stoppedAt === null;
    // The tick value itself is unused — we read `Date.now()` via
    // `formatElapsedMs` at render time. The hook's job is only to
    // bump a state value so React re-renders this leaf each second
    // while the agent is running. When stopped, the hook clears its
    // interval and the value freezes at `stoppedAt - startedAt`.
    const tick = useElapsedTick(isRunning);
    void tick;
    return (
        <span className={className} data-testid={testId}>
            {formatElapsedMs(startedAt, stoppedAt)}
        </span>
    );
}

export default ElapsedTickSpan;
